import { rem } from "@certn/cascadia";
import {
  CheckboxField,
  Container,
  Grid,
  GridItem,
  PhoneNumberInputField,
  PrimaryButton,
  SectionMessage,
  TextInputField,
} from "@certn/cascadia-react";
import { GhostIconButton } from "@certn/cascadia-react/components/button/GhostIconButton";
import { phoneNumberSchema } from "@certn/cascadia-react/components/inputs/phone-number/schema";
import { getUserCountry } from "@certn/utils";
import { css } from "@emotion/css";
import { Field, Form, Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { bool, object, string } from "yup";

import { useAuth } from "./hooks";
import { AppTitle } from "../../components/AppTitle";
import { ClickwrapCheckBox } from "../../components/Clickwrap";
import { useFormSubmit } from "../../hooks/api/useSubmit";

interface Props {
  onSuccess: () => void;
}

export function ApplicantDetailsPage(props: Props) {
  const { onSuccess } = props;
  const { t } = useTranslation();
  const { logout } = useAuth();

  const submit = useFormSubmit("/internal/people/me/create/", {
    onSuccess,
  });

  // 1+ letters with spaces, apostrophes and hyphens allowed anywhere except first and last character
  const validNameRegex = /^((\p{L}+)|(\p{L}[\p{L}\s'-]*\p{L}))$/u;
  const validationText = t("Please only use letters and numbers");

  const schema = object({
    first_name: string()
      .matches(validNameRegex, validationText)
      .required(t<string>("Enter legal first name.")),
    last_name: string()
      .matches(validNameRegex, validationText)
      .required(t<string>("Enter legal last name.")),
    consent_tou_and_privacy: bool().oneOf(
      [true],
      t<string>(
        "Please agree to Certn's Privacy Policy and Website Terms to continue."
      )
    ),
  }).concat(phoneNumberSchema);
  return (
    <Container>
      <div
        className={css(
          css({
            left: `-${rem("space-3")}`,
            position: "relative",
          })
        )}
      >
        <GhostIconButton
          size="medium"
          icon="close"
          onPress={logout}
          testID="sign-up-form-close-button"
        />
      </div>
      <AppTitle
        title={t("Let's get you set up")}
        subtitle={t("Tell us about yourself to finish creating your account.")}
      />
      <Formik
        initialValues={{
          first_name: "",
          last_name: "",
          phone_number: {
            number: "",
            national_number: "",
            country: getUserCountry(),
            calling_code: "",
          },
          consent_tou_and_privacy: false,
          accept_marketing: false,
        }}
        onSubmit={submit}
        validationSchema={schema}
      >
        {({ status, isSubmitting }) => {
          return (
            <Form>
              <Grid>
                {status && (
                  <GridItem>
                    <SectionMessage
                      type="danger"
                      sectionTitle={t("System Error")}
                      icon="info"
                    >
                      {status}
                    </SectionMessage>
                  </GridItem>
                )}
                <GridItem>
                  <Field
                    as={TextInputField}
                    isRequired
                    label={t("Legal first name")}
                    name="first_name"
                    testID="sign-up-form-legal-first-name"
                  />
                </GridItem>
                <GridItem>
                  <Field
                    as={TextInputField}
                    isRequired
                    label={t("Legal last name")}
                    name="last_name"
                    testID="sign-up-form-legal-last-name"
                  />
                </GridItem>
                <GridItem>
                  <Field
                    name="phone_number"
                    label={t("Phone number")}
                    as={PhoneNumberInputField}
                    isRequired
                    testID="sign-up-form-phone-number"
                  />
                </GridItem>
                <GridItem>
                  <Field
                    name="accept_marketing"
                    as={CheckboxField}
                    testID="sign-up-form-news-and-offers"
                  >
                    <Trans>
                      I want to receive news, feature updates, discounts, and
                      offers from Certn. (Optional)
                    </Trans>
                  </Field>
                </GridItem>
                <GridItem>
                  <ClickwrapCheckBox testID="sign-up-form-PP-and-t&cs" />
                </GridItem>
                <GridItem>
                  <PrimaryButton
                    fullWidth
                    type="submit"
                    size="large"
                    isLoading={isSubmitting}
                    testID="sign-up-form-continue-button"
                  >
                    {t("Continue")}
                  </PrimaryButton>
                </GridItem>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
}
